/**
 * Section "Notes" : textarea editable avec sauvegarde auto debounced 500ms.
 * Indicator visible (saving / saved / error).
 */

import { useState, useEffect, useCallback } from 'react';
import * as ceeApi from '../../api/cee.js';
import { useDebouncedCallback } from '../lib/useDebouncedCallback.js';

/**
 * @param {{ dossier: object | null, readOnly?: boolean }} props
 */
export default function NotesSection({ dossier, readOnly = false }) {
  const [value, setValue] = useState(dossier?.notes || '');
  const [savedValue, setSavedValue] = useState(dossier?.notes || '');
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState('');

  // Sync si dossier change
  useEffect(() => {
    setValue(dossier?.notes || '');
    setSavedValue(dossier?.notes || '');
    setState('idle');
  }, [dossier?.id, dossier?.notes]);

  const save = useCallback(async (next) => {
    if (!dossier?.id) return;
    if (readOnly) return;
    setState('saving');
    setError('');
    try {
      await ceeApi.updateDossier(dossier.id, { notes: next });
      setSavedValue(next);
      setState('saved');
    } catch (err) {
      setError(err.message || 'Erreur sauvegarde');
      setState('error');
    }
  }, [dossier?.id, readOnly]);

  const debouncedSave = useDebouncedCallback(save, 500);

  const onChange = (e) => {
    const v = e.target.value;
    if (readOnly) return;
    setValue(v);
    if (v === savedValue) {
      // Codex S2.6 P2 : revert pendant le debounce -> cancel le pending save
      debouncedSave.cancel?.();
      setState('saved');
      return;
    }
    setState('saving');
    debouncedSave(v);
  };

  if (!dossier) return null;

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold text-[#0f172a]">Notes</h2>
        <span className="text-xs text-[#94a3b8]">
          {state === 'saving' && 'Enregistrement...'}
          {state === 'saved' && 'Enregistre'}
          {state === 'error' && <span className="text-red-600">{error}</span>}
        </span>
      </div>
      <textarea
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        rows={5}
        placeholder="Commentaires libres sur le dossier (visible par l equipe)..."
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none ${readOnly ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
      />
    </section>
  );
}
